import React, { useEffect, useMemo, useState } from "react";
import { message, Modal, Pagination, Select } from "antd";
import { FaSearch } from "react-icons/fa";
import { AiOutlineEye } from "react-icons/ai";
import { BsBug, BsLightbulb } from "react-icons/bs";
import Load from "../../Load/Load";
import { getAdminFeedback } from "../../../Web Apis/webApis";

const { Option } = Select;

const AdminFeedbackManagement = () => {
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, bug: 0, suggestion: 0 });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 1,
  });
  const [typeFilter, setTypeFilter] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [showDetails, setShowDetails] = useState(false);

  const fetchFeedback = async ({ page = 1, type = typeFilter, q = searchQuery } = {}) => {
    try {
      setLoading(true);
      const response = await getAdminFeedback({
        page,
        limit: pagination.limit,
        type,
        q,
      });

      if (response?.data?.success) {
        setRows(response?.data?.data || []);
        setPagination((prev) => ({
          ...prev,
          ...(response?.data?.pagination || {}),
        }));
        setStats(response?.data?.stats || { total: 0, bug: 0, suggestion: 0 });
      }
    } catch (error) {
      console.log("Error fetching admin feedback", error?.message);
      message.error("Failed to load feedback reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const initialLoad = async () => {
      try {
        setLoading(true);
        const response = await getAdminFeedback({
          page: 1,
          limit: pagination.limit,
          type: "",
          q: "",
        });
        if (response?.data?.success) {
          setRows(response?.data?.data || []);
          setPagination((prev) => ({
            ...prev,
            ...(response?.data?.pagination || {}),
          }));
          setStats(response?.data?.stats || { total: 0, bug: 0, suggestion: 0 });
        }
      } catch (error) {
        console.log("Error fetching admin feedback", error?.message);
        message.error("Failed to load feedback reports");
      } finally {
        setLoading(false);
      }
    };
    initialLoad();
  }, [pagination.limit]);

  const bugCount = useMemo(
    () => rows.filter((item) => item.type === "bug").length,
    [rows],
  );
  const suggestionCount = useMemo(
    () => rows.filter((item) => item.type === "suggestion").length,
    [rows],
  );

  const handleApplyFilters = () => {
    fetchFeedback({ page: 1 });
  };

  const handleClearFilters = () => {
    setTypeFilter("");
    setSearchQuery("");
    fetchFeedback({ page: 1, type: "", q: "" });
  };

  const openDetails = (item) => {
    setSelectedFeedback(item);
    setShowDetails(true);
  };

  return (
    <div className="p-6 mt-16">
      <div className="mb-6">
        <h2 className="text-3xl font-bold text-gray-800">Feedback & Bug Reports</h2>
        <p className="text-gray-600 mt-1">
          Mobile app feedback submitted by users (bugs + suggestions)
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white p-4 rounded-lg shadow-md">
          <p className="text-blue-100 text-sm">Total Reports</p>
          <p className="text-2xl font-bold">{stats.total || 0}</p>
        </div>
        <div className="bg-gradient-to-r from-red-500 to-red-600 text-white p-4 rounded-lg shadow-md">
          <p className="text-red-100 text-sm">Total Bugs</p>
          <p className="text-2xl font-bold">{stats.bug || 0}</p>
        </div>
        <div className="bg-gradient-to-r from-emerald-500 to-emerald-600 text-white p-4 rounded-lg shadow-md">
          <p className="text-emerald-100 text-sm">Total Suggestions</p>
          <p className="text-2xl font-bold">{stats.suggestion || 0}</p>
        </div>
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white p-4 rounded-lg shadow-md">
          <p className="text-purple-100 text-sm">Visible Page Split</p>
          <p className="text-2xl font-bold">{bugCount} / {suggestionCount}</p>
        </div>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
            <div className="relative">
              <FaSearch className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by title or description..."
                className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
            <Select
              value={typeFilter}
              onChange={setTypeFilter}
              className="w-full"
              allowClear
            >
              <Option value="">All Types</Option>
              <Option value="bug">Bug</Option>
              <Option value="suggestion">Suggestion</Option>
            </Select>
          </div>
          <div className="flex items-end gap-2">
            <button
              onClick={handleApplyFilters}
              className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition duration-200 w-full"
            >
              Apply
            </button>
            <button
              onClick={handleClearFilters}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition duration-200 w-full"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">#</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {rows.length > 0 ? (
                rows.map((item, index) => (
                  <tr key={item._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {(pagination.page - 1) * pagination.limit + index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${
                          item.type === "bug"
                            ? "bg-red-100 text-red-800"
                            : "bg-emerald-100 text-emerald-800"
                        }`}
                      >
                        {item.type === "bug" ? <BsBug /> : <BsLightbulb />}
                        {item.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900 max-w-sm">
                      <p className="font-medium">{item.title}</p>
                      <p className="text-xs text-gray-500 truncate">{item.description}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-900">
                      <p>{item?.user?.name || "Unknown User"}</p>
                      <p className="text-xs text-gray-500">{item?.user?.email || "-"}</p>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(item.createdAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => openDetails(item)}
                        className="text-blue-600 hover:text-blue-900 p-1"
                        title="View details"
                      >
                        <AiOutlineEye className="text-lg" />
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="6" className="px-6 py-12 text-center text-gray-500">
                    {loading ? "Loading feedback..." : "No feedback reports found"}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {pagination.total > pagination.limit && (
        <div className="flex justify-center mt-6">
          <Pagination
            current={pagination.page}
            total={pagination.total}
            pageSize={pagination.limit}
            onChange={(page) => fetchFeedback({ page })}
            showSizeChanger={false}
          />
        </div>
      )}

      {showDetails && (
        <Modal
          title="Feedback Details"
          open={showDetails}
          onCancel={() => setShowDetails(false)}
          footer={[
            <button
              key="close"
              onClick={() => setShowDetails(false)}
              className="bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Close
            </button>,
          ]}
          width={700}
        >
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-500">Type</p>
                <p className="font-medium capitalize">{selectedFeedback?.type}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Created At</p>
                <p className="font-medium">
                  {selectedFeedback?.createdAt
                    ? new Date(selectedFeedback.createdAt).toLocaleString()
                    : "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User Name</p>
                <p className="font-medium">{selectedFeedback?.user?.name || "Unknown User"}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">User Email</p>
                <p className="font-medium break-all">{selectedFeedback?.user?.email || "-"}</p>
              </div>
            </div>

            <div>
              <p className="text-sm text-gray-500">Title</p>
              <p className="font-medium">{selectedFeedback?.title || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500">Description</p>
              <p className="font-medium whitespace-pre-wrap">{selectedFeedback?.description || "-"}</p>
            </div>
            <div>
              <p className="text-sm text-gray-500 mb-2">Screenshot</p>
              {selectedFeedback?.screenshotUrl ? (
                <a
                  href={selectedFeedback.screenshotUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-600 underline break-all"
                >
                  {selectedFeedback.screenshotUrl}
                </a>
              ) : (
                <p className="text-gray-500">No screenshot attached</p>
              )}
            </div>
          </div>
        </Modal>
      )}

      {loading && <Load />}
    </div>
  );
};

export default AdminFeedbackManagement;
